import { Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Ability, subject } from '@casl/ability';
import * as moment from 'moment';
import { ProjectsService } from '../../../../../../core/projects.service';
import { CommonService } from '../../../../../../../../shared/services/common.service';
import { AuthService } from '../../../../../../../../shared/services/auth.service';
import { RiskRegisterService } from '../risk-register.service';
import { Risk, RiskConfig, RiskInput, RiskResponseType } from '../risk-register.models';
import { BAND_MAP, RESPONSE_TYPES, RISK_ATTACHMENT_ACCEPT, RISK_ATTACHMENT_TAG, RiskMediaFile, bandOf, scoreOf, serverErrorMessage, toRiskMedia } from '../risk-register.util';
import { RiskDetailPanelComponent } from '../risk-detail-panel/risk-detail-panel.component';

export type RiskFormMode = 'create' | 'propose' | 'edit';

const LEVEL = [Validators.required, Validators.min(1), Validators.max(5)];

/** Create / propose / edit a risk in the right panel (spec §5.5). */
@Component({
  selector: 'app-risk-form',
  templateUrl: './risk-form.component.html',
  styleUrls: ['./risk-form.component.scss'],
  standalone: false,
})
export class RiskFormComponent implements OnInit, OnDestroy {
  @Input() data: { projectId: string; mode: RiskFormMode; risk?: Risk; config?: RiskConfig; members?: { id: string; name: string }[] } = { projectId: null, mode: 'create' };
  @Input() options: Record<string, unknown> = {};

  form = new FormGroup({
    description: new FormControl('', [Validators.required, Validators.pattern(/\S/), Validators.maxLength(4000)]),
    project_impact: new FormControl('', Validators.maxLength(4000)),
    probability: new FormControl<number>(null, LEVEL),
    impact: new FormControl<number>(null, LEVEL),
    risk_areas: new FormControl<string[]>([], Validators.required),
    discipline: new FormControl<string>(null),
    owner_account_id: new FormControl<string>(null),
    response_type: new FormControl<string>(null),
    response_strategy: new FormControl('', Validators.maxLength(4000)),
    response_action: new FormControl('', Validators.maxLength(4000)),
    identified_at: new FormControl<moment.Moment | string>(null),
    internal: new FormControl(false),
  });
  levels = [1, 2, 3, 4, 5];
  responseTypes = RESPONSE_TYPES;
  config: RiskConfig = { risk_areas: [], disciplines: [] };
  memberOptions: { value: string; label: string }[] = [];
  disciplineOptions: { value: string; label: string }[] = [];
  files: RiskMediaFile[] = [];
  readonly attachmentTag = RISK_ATTACHMENT_TAG;
  readonly attachmentAccept = RISK_ATTACHMENT_ACCEPT;
  saving = false;
  isContractor = false;
  canManage = false;
  private readonly onDestroy = new Subject<void>();

  private router = inject(Router);
  private projectsService = inject(ProjectsService);
  private commonService = inject(CommonService);
  private authService = inject(AuthService);
  private riskService = inject(RiskRegisterService);
  private ability = inject(Ability);

  get mode(): RiskFormMode { return this.data.mode; }
  get title(): string { return this.mode === 'edit' ? `Edit risk ${this.data.risk?.risk_no}` : this.mode === 'propose' ? 'Propose a risk' : 'New risk'; }
  get submitLabel(): string { return this.mode === 'edit' ? 'Save changes' : this.mode === 'propose' ? 'Submit for approval' : 'Save'; }
  get ownerLabel(): string { return this.mode === 'propose' ? 'Suggested owner' : 'Owner'; }
  get submitDisabled(): boolean { return this.form.invalid || this.saving || (this.mode === 'edit' && !this.form.dirty); }
  /** Display only: the server recomputes the score and never reads it from the body. */
  get score(): number {
    const { probability, impact } = this.form.value;
    return probability && impact ? scoreOf(probability, impact) : 0;
  }
  get bandLabel(): string { return this.score ? BAND_MAP[bandOf(this.score)].label : ''; }
  get showInternal(): boolean { return this.canManage && !this.isContractor; }

  ngOnInit(): void {
    const me = this.authService.getCurrentUser();
    const role = me?.roles?.find((r: { project_id: string; role: string }) => r.project_id === this.data.projectId)?.role || me?.role || '';
    this.isContractor = role.startsWith('contractor');
    this.canManage = this.ability.can('update', subject('Risk', { project_id: this.data.projectId }));

    if (this.data.config) this.applyConfig(this.data.config);
    else this.riskService.config(this.data.projectId).pipe(takeUntil(this.onDestroy)).subscribe(c => this.applyConfig(c));

    if (this.data.members) this.setMembers(this.data.members);
    else {
      this.projectsService.fetchProjectAccounts({ projectId: this.data.projectId, qp: { start: 0, total: 500 } })
        .pipe(takeUntil(this.onDestroy))
        .subscribe((resp: { data?: { id: string; first_name: string; last_name: string }[] }) => {
          this.setMembers((resp?.data || []).map(a => ({ id: a.id, name: `${a.first_name} ${a.last_name}`.trim() })));
        });
    }

    const r = this.data.risk;
    if (r) {
      this.form.patchValue({
        description: r.description, project_impact: r.project_impact || '', probability: r.probability, impact: r.impact,
        risk_areas: r.risk_areas || [], discipline: r.discipline, owner_account_id: r.owner?.id || null,
        response_type: r.response_type, response_strategy: r.response_strategy || '', response_action: r.response_action || '',
        identified_at: r.identified_at ? moment(String(r.identified_at).slice(0, 10), 'YYYY-MM-DD') : null, internal: !!r.internal,
      });
      this.setFiles((r.attachments || []).map(toRiskMedia));
    }
    this.commonService.uploadedFilesInfo.pipe(takeUntil(this.onDestroy)).subscribe(data => {
      const items: RiskMediaFile[] = JSON.parse(JSON.stringify(Array.isArray(data) ? data : [data]));
      this.setFiles([...this.files, ...items.filter(f => f?.id).map(toRiskMedia)]);
      this.form.markAsDirty();
      this.projectsService.isAnyFormDirty.next(true);
    });
    this.form.valueChanges.pipe(takeUntil(this.onDestroy)).subscribe(() => this.projectsService.isAnyFormDirty.next(this.form.dirty));
  }

  private applyConfig(c: RiskConfig): void {
    this.config = { risk_areas: c?.risk_areas || [], disciplines: c?.disciplines || [] };
    // Keep values already on the risk selectable even if the config no longer lists them.
    const stored = this.data.risk;
    const disciplines = [...this.config.disciplines];
    if (stored?.discipline && !disciplines.includes(stored.discipline)) disciplines.push(stored.discipline);
    this.disciplineOptions = disciplines.map(d => ({ value: d, label: d }));
    const areas = [...this.config.risk_areas];
    for (const a of stored?.risk_areas || []) if (!areas.includes(a)) areas.push(a);
    this.config.risk_areas = areas;
  }

  /** Members sorted by name; a stored owner who left the project stays selectable (spec review focus 1). */
  private setMembers(members: { id: string; name: string }[]): void {
    const list = members.map(m => ({ value: m.id, label: m.name }));
    const owner = this.data.risk?.owner;
    if (owner && !list.some(m => m.value === owner.id)) list.push({ value: owner.id, label: `${owner.first_name} ${owner.last_name}`.trim() });
    this.memberOptions = list.sort((a, b) => a.label.localeCompare(b.label));
  }

  pick(field: 'probability' | 'impact', v: number): void { this.form.get(field).setValue(v); this.form.markAsDirty(); }
  toggleArea(a: string): void {
    const cur = this.form.value.risk_areas || [];
    this.form.get('risk_areas').setValue(cur.includes(a) ? cur.filter(x => x !== a) : [...cur, a]);
    this.form.markAsDirty();
  }
  hasArea(a: string): boolean { return (this.form.value.risk_areas || []).includes(a); }

  removeFile(f: RiskMediaFile): void {
    this.setFiles(this.files.filter(x => x.id !== f.id));
    this.form.markAsDirty();
    this.projectsService.isAnyFormDirty.next(true);
  }

  /** app-pictures-section-with-tags renders from ProjectsService.mediaData, not its input (punchlist pattern). */
  private setFiles(files: RiskMediaFile[]): void {
    this.files = files;
    setTimeout(() => this.projectsService.mediaData.next(this.files));
  }

  /** The request body. `score` is deliberately absent; contractors never send internal = true. */
  payload(): RiskInput {
    const v = this.form.value;
    const d = v.identified_at;
    const identified = d ? (moment.isMoment(d) ? d.format('YYYY-MM-DD') : String(d).slice(0, 10)) : null;
    return {
      description: v.description.trim(), project_impact: v.project_impact?.trim() || null,
      probability: v.probability, impact: v.impact, risk_areas: v.risk_areas || [],
      discipline: v.discipline || null, owner_account_id: v.owner_account_id || null,
      response_type: (v.response_type as RiskResponseType) || null, response_strategy: v.response_strategy?.trim() || null,
      response_action: v.response_action?.trim() || null, identified_at: identified,
      internal: this.showInternal ? !!v.internal : false, attachments: this.files.map(f => f.id),
    };
  }

  /**
   * Edit sends only what changed (PATCH accepts a partial body). Resending an unchanged owner
   * fails for an owner who has left the project (API: "Owner must be a member"), and
   * attachments are unlinked/relinked wholesale server-side, so both go only when different.
   */
  private changedFields(full: RiskInput): Partial<RiskInput> {
    const r = this.data.risk;
    const same = (a: unknown, b: unknown): boolean => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
    const before: Partial<RiskInput> = {
      description: r.description, project_impact: r.project_impact, probability: r.probability, impact: r.impact,
      risk_areas: r.risk_areas || [], discipline: r.discipline, owner_account_id: r.owner?.id ?? null,
      response_type: r.response_type, response_strategy: r.response_strategy, response_action: r.response_action,
      internal: !!r.internal, identified_at: r.identified_at ? String(r.identified_at).slice(0, 10) : null,
      attachments: (r.attachments || []).map(f => f.id).sort(),
    };
    const out: Partial<RiskInput> = {};
    for (const key of Object.keys(full) as (keyof RiskInput)[]) {
      const now = key === 'attachments' ? [...full.attachments].sort() : full[key];
      if (!same(now, before[key])) (out as Record<string, unknown>)[key] = full[key];
    }
    return out;
  }

  submit(): void {
    if (this.form.invalid || this.saving) { this.form.markAllAsTouched(); return; }
    this.saving = true;
    const full = this.payload();
    const call = this.mode === 'edit'
      ? this.riskService.update(this.data.risk.id, this.changedFields(full))
      : this.mode === 'propose' ? this.riskService.propose(this.data.projectId, full) : this.riskService.create(this.data.projectId, full);
    call.pipe(takeUntil(this.onDestroy)).subscribe({
      next: risk => {
        this.saving = false;
        this.projectsService.isAnyFormDirty.next(false);
        this.commonService.notification(this.mode === 'propose' ? 'Risk submitted for approval' : 'Risk saved', 'success');
        this.riskService.refresh$.next();
        const id = risk?.id || this.data.risk?.id;
        this.router.navigate([], { queryParams: { riskId: id }, queryParamsHandling: 'merge' });
        this.projectsService.openRightPanel.next({ component: RiskDetailPanelComponent, data: { riskId: id, projectId: this.data.projectId } });
      },
      error: err => { this.saving = false; this.commonService.notification(serverErrorMessage(err?.error, 'Save failed'), 'error'); },
    });
  }

  cancel(): void {
    this.projectsService.isAnyFormDirty.next(false);
    if (this.data.risk) this.projectsService.openRightPanel.next({ component: RiskDetailPanelComponent, data: { riskId: this.data.risk.id, projectId: this.data.projectId } });
    else this.projectsService.closeRightPanel.next();
  }

  ngOnDestroy(): void { this.onDestroy.next(); this.onDestroy.complete(); }
}
